import { useEffect, useState } from "react";
import { Leaf, ChevronRight, Loader2, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function Onboarding({ onComplete }) {
    const [presets, setPresets] = useState([]);
    const [step, setStep] = useState(0);
    const [tipo, setTipo] = useState("");
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        ragione_sociale: "",
        tipo_attivita: "",
        titolare: "",
        rappresentante_legale: "",
        responsabile_haccp: "",
        indirizzo: "",
        citta: "",
        cap: "",
        provincia: "",
        sede_legale: "",
        partita_iva: "",
        codice_fiscale: "",
        numero_rea: "",
        codice_ateco: "",
        telefono: "",
        email: "",
    });

    useEffect(() => {
        api.get("/activity-presets").then((r) => setPresets(r.data));
    }, []);

    async function submit() {
        const finalTipo = tipo || "ortofrutta"; // silent fallback, don't block user
        const finalRagione = form.ragione_sociale.trim() || "Mio negozio"; // silent fallback
        setBusy(true);
        try {
            await api.post("/onboarding", { ...form, ragione_sociale: finalRagione, tipo_attivita_key: finalTipo });
            toast.success("App configurata!");
            onComplete?.();
        } catch (e) {
            // Show the actual backend error so the user (and we) know what failed
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            let msg = formatApiErrorDetail(detail);
            if (!detail) {
                if (status === 401) msg = "Sessione scaduta. Esci e accedi di nuovo.";
                else if (status === 402) msg = "Abbonamento scaduto. Rinnova per continuare.";
                else if (!status) msg = "Connessione al server fallita. Controlla la rete.";
                else msg = `Errore ${status}: riprova o contatta supporto.`;
            }
            console.error("Onboarding failed", { status, detail, error: e });
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen bg-bone flex items-start sm:items-center justify-center p-4 sm:p-8" data-testid="onboarding-page">
            <div className="card-soft p-6 sm:p-10 max-w-2xl w-full">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-forest text-white grid place-items-center">
                        <Leaf size={22} />
                    </div>
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-forest-500">
                            Configurazione iniziale
                        </p>
                        <h1 className="font-display text-2xl font-semibold text-forest-dark">
                            Benvenuto in HACCP Manager
                        </h1>
                    </div>
                </div>

                {/* Stepper */}
                <div className="flex items-center gap-2 mb-6">
                    {["Tipo attività", "Dati negozio"].map((label, idx) => (
                        <div key={label} className="flex items-center gap-2 text-xs">
                            <div className={`w-7 h-7 rounded-full grid place-items-center font-semibold ${
                                step > idx ? "bg-forest text-white" : step === idx ? "bg-forest text-white" : "bg-bone-2 text-stone-500"
                            }`}>
                                {step > idx ? <Check size={14} /> : idx + 1}
                            </div>
                            <span className={step === idx ? "font-semibold text-forest-dark" : "text-stone-500"}>{label}</span>
                            {idx < 1 && <ChevronRight size={14} className="text-stone-300 ml-2" />}
                        </div>
                    ))}
                </div>

                {step === 0 && (
                    <div data-testid="onb-step-1">
                        <h2 className="font-display text-lg font-semibold text-forest-dark mb-2">
                            Che attività gestisci?
                        </h2>
                        <p className="text-sm text-stone-500 mb-5">
                            La checklist HACCP, i frigoriferi predefiniti e il manuale saranno personalizzati per la tua attività.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {presets.map((p) => (
                                <button
                                    key={p.key}
                                    onClick={() => setTipo(p.key)}
                                    data-testid={`preset-${p.key}`}
                                    className={`rounded-xl border-2 p-4 text-left transition-all ${
                                        tipo === p.key
                                            ? "border-forest bg-forest-50"
                                            : "border-soft bg-white hover:border-forest/40"
                                    }`}
                                >
                                    <p className="font-semibold text-forest-dark">{p.label}</p>
                                    <p className="text-xs text-stone-500 mt-1">{p.tipo_attivita_text}</p>
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => { if (!tipo) setTipo("ortofrutta"); setStep(1); }}
                            className="btn-primary mt-6 inline-flex items-center gap-2"
                            data-testid="onb-next"
                        >
                            Continua <ChevronRight size={16} />
                        </button>
                    </div>
                )}

                {step === 1 && (
                    <div data-testid="onb-step-2">
                        <h2 className="font-display text-lg font-semibold text-forest-dark mb-2">
                            Dati del tuo negozio
                        </h2>
                        <p className="text-sm text-stone-500 mb-5">
                            Sono usati nel manuale HACCP e nei report ASL. Puoi modificarli in qualsiasi momento.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {[
                                ["ragione_sociale", "Ragione sociale / Nome attività *"],
                                ["tipo_attivita", "Insegna / nome commerciale"],
                                ["titolare", "Titolare"],
                                ["rappresentante_legale", "Rappresentante legale"],
                                ["responsabile_haccp", "Responsabile HACCP"],
                                ["indirizzo", "Indirizzo operativo"],
                                ["citta", "Città"],
                                ["cap", "CAP"],
                                ["provincia", "Provincia"],
                                ["sede_legale", "Sede legale (se diversa)"],
                                ["partita_iva", "Partita IVA"],
                                ["codice_fiscale", "Codice Fiscale"],
                                ["numero_rea", "N° REA (Camera di Commercio)"],
                                ["codice_ateco", "Codice ATECO"],
                                ["telefono", "Telefono"],
                                ["email", "Email"],
                            ].map(([k, l]) => (
                                <label key={k} className="block">
                                    <span className="text-xs font-semibold text-stone-600">{l}</span>
                                    <input
                                        className="input-soft mt-1"
                                        value={form[k]}
                                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                        data-testid={`onb-${k}`}
                                    />
                                </label>
                            ))}
                        </div>
                        <div className="flex gap-3 mt-6 flex-wrap">
                            <button onClick={() => setStep(0)} className="btn-ghost text-sm" data-testid="onb-back">Indietro</button>
                            <button
                                onClick={submit}
                                disabled={busy}
                                className="btn-primary inline-flex items-center gap-2"
                                data-testid="onb-finish"
                            >
                                {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                Configura e inizia
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
